import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { Upload, X, ArrowLeft, Save, Plus, Video as VideoIcon, RefreshCw } from "lucide-react";
import { imgUrl } from "../../components/PropertyCard";

// SEO-friendly slug: strip accents, lowercase, replace non-alphanum with -
const slugify = (s) => (s || "")
  .toString()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const empty = {
  title: "", slug: "", description: "",
  surface_min: 270, surface_max: 450, price_monthly: 2500,
  location: "Cercottes, 45520", address: "98 Route Nationale 20, 45520 Cercottes",
  features: [], modular: true, available: true, status: "disponible",
  available_from: "",
  images: [], videos: [],
};

export default function PropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [featureInput, setFeatureInput] = useState("");
  const [videoInput, setVideoInput] = useState({ kind: "youtube", url: "", title: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  // Track if the user manually edited the slug — if not, keep it in sync with the title
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/properties/${id}`).then(r => {
        setForm({ ...empty, ...r.data });
        setSlugTouched(true); // existing slug is locked unless user explicitly retypes it
      });
    }
  }, [id]);

  const set = (k, v) => setForm(prev => {
    const next = { ...prev, [k]: v };
    // Auto-sync slug with title until the user types in the slug field
    if (k === "title" && !slugTouched) {
      next.slug = slugify(v);
    }
    return next;
  });

  const setSlug = (v) => {
    setSlugTouched(true);
    setForm(prev => ({ ...prev, slug: slugify(v) }));
  };

  const regenerateSlug = () => {
    setSlugTouched(false);
    setForm(prev => ({ ...prev, slug: slugify(prev.title) }));
  };

  // Live URL preview
  const publicUrl = useMemo(() => {
    const s = form.slug || slugify(form.title) || "(à venir)";
    return `/locaux/${s}`;
  }, [form.slug, form.title]);

  const addFeature = () => {
    if (!featureInput.trim()) return;
    set("features", [...form.features, featureInput.trim()]);
    setFeatureInput("");
  };
  const removeFeature = (i) => set("features", form.features.filter((_, idx) => idx !== i));

  const uploadFiles = async (files) => {
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ path: data.path, url: data.url });
      } catch (e) {
        toast.error("Échec upload: " + (e.response?.data?.detail || file.name));
      }
    }
    set("images", [...form.images, ...uploaded]);
    setUploading(false);
  };
  const removeImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));

  const uploadVideoFile = async (file) => {
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("videos", [...form.videos, { kind: "upload", url: data.url, title: file.name }]);
      toast.success("Vidéo uploadée");
    } catch (e) {
      toast.error("Échec upload vidéo: " + (e.response?.data?.detail || ""));
    } finally { setUploadingVideo(false); }
  };

  const addVideoUrl = () => {
    if (!videoInput.url.trim()) return;
    set("videos", [...form.videos, { ...videoInput, url: videoInput.url.trim(), title: videoInput.title || "Vidéo" }]);
    setVideoInput({ kind: "youtube", url: "", title: "" });
  };
  const removeVideo = (i) => set("videos", form.videos.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        surface_min: parseInt(form.surface_min),
        surface_max: parseInt(form.surface_max),
        price_monthly: parseFloat(form.price_monthly),
        slug: form.slug ? slugify(form.slug) : slugify(form.title),
        available_from: form.available_from || null,
      };
      if (id) {
        await api.put(`/admin/properties/${id}`, payload);
        toast.success("Local mis à jour");
      } else {
        await api.post("/admin/properties", payload);
        toast.success("Local créé");
      }
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-4xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 flex items-center gap-2" data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Retour au tableau de bord
        </button>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-8">{id ? "Modifier le local" : "Nouveau local"}</h1>

        <form onSubmit={submit} className="bg-white border border-slate-900/15 p-8 md:p-10 space-y-6" data-testid="property-form">
          <Field label="Titre *">
            <input required value={form.title} onChange={e => set("title", e.target.value)} className="input" data-testid="pf-title" />
          </Field>
          <Field label="URL publique (slug SEO)">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-slate-500 shrink-0">/locaux/</span>
              <input
                value={form.slug}
                onChange={e => setSlug(e.target.value)}
                placeholder="se-genere-automatiquement-depuis-le-titre"
                className="input flex-1 font-mono text-sm"
                data-testid="pf-slug"
              />
              <button
                type="button"
                onClick={regenerateSlug}
                title="Régénérer depuis le titre"
                className="btn-brutal bg-white border border-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                data-testid="pf-regenerate-slug"
              >
                <RefreshCw className="w-3 h-3"/>
              </button>
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Aperçu : <span className="font-mono">{publicUrl}</span>
              {form.slug && (
                <span className="ml-2 italic">— gardez l'URL stable une fois indexée par Google.</span>
              )}
            </div>
          </Field>
          <Field label="Description *">
            <textarea required rows={4} value={form.description} onChange={e => set("description", e.target.value)} className="input resize-none" data-testid="pf-description" />
          </Field>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Surface min (m²)">
              <input type="number" value={form.surface_min} onChange={e => set("surface_min", e.target.value)} className="input" data-testid="pf-surface-min" />
            </Field>
            <Field label="Surface max (m²)">
              <input type="number" value={form.surface_max} onChange={e => set("surface_max", e.target.value)} className="input" data-testid="pf-surface-max" />
            </Field>
            <Field label="Loyer mensuel HT (€)">
              <input type="number" step="0.01" value={form.price_monthly} onChange={e => set("price_monthly", e.target.value)} className="input" data-testid="pf-price" />
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Statut">
              <select value={form.status} onChange={e => set("status", e.target.value)} className="input" data-testid="pf-status">
                <option value="disponible">Disponible</option>
                <option value="occupe">Occupé</option>
                <option value="loue">Loué</option>
              </select>
            </Field>
            <Field label="Disponible à partir du (vide = immédiat)">
              <input type="date" value={form.available_from || ""} onChange={e => set("available_from", e.target.value)} className="input" data-testid="pf-available-from" />
            </Field>
          </div>
          <div className="grid md:grid-cols-1 gap-4">
            <Field label="Localisation"><input value={form.location} onChange={e => set("location", e.target.value)} className="input" /></Field>
          </div>
          <Field label="Adresse"><input value={form.address} onChange={e => set("address", e.target.value)} className="input" /></Field>

          <Field label="Caractéristiques">
            <div className="flex gap-2 mb-3">
              <input value={featureInput} onChange={e => setFeatureInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addFeature())} className="input flex-1" placeholder="ex: Triphasé 63A" data-testid="pf-feature-input" />
              <button type="button" onClick={addFeature} className="btn-brutal bg-white border border-slate-900 px-4 font-bold uppercase tracking-wider text-xs" data-testid="pf-add-feature">Ajouter</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.features.map((f, i) => (
                <span key={`feat-${f}-${i}`} className="tag flex items-center gap-2">
                  {f} <button type="button" onClick={() => removeFeature(i)} className="text-red-600"><X className="w-3 h-3"/></button>
                </span>
              ))}
            </div>
          </Field>

          <Field label="Photos">
            <label className="block border-2 border-dashed border-slate-900/30 p-8 text-center cursor-pointer hover:border-slate-900 transition-colors" data-testid="pf-upload-zone">
              <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              <span className="text-sm font-semibold">{uploading ? "Upload en cours..." : "Cliquez pour ajouter des photos"}</span>
              <input type="file" multiple accept="image/*" onChange={e => uploadFiles(Array.from(e.target.files))} className="hidden" data-testid="pf-file-input" />
            </label>
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {form.images.map((img, i) => (
                  <div key={img.path || img.url || `img-${i}`} className="relative aspect-[4/3] border border-slate-900/20">
                    <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-600 text-white p-1" data-testid={`remove-img-${i}`}><X className="w-3 h-3"/></button>
                  </div>
                ))}
              </div>
            )}
          </Field>

          <Field label="Vidéos">
            <div className="space-y-3">
              <div className="grid md:grid-cols-[140px_1fr_auto] gap-2">
                <select value={videoInput.kind} onChange={e => setVideoInput({ ...videoInput, kind: e.target.value })} className="input" data-testid="pf-video-kind">
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                </select>
                <input placeholder="URL YouTube / Vimeo" value={videoInput.url} onChange={e => setVideoInput({ ...videoInput, url: e.target.value })} className="input" data-testid="pf-video-url" />
                <button type="button" onClick={addVideoUrl} className="btn-brutal bg-white border border-slate-900 px-4 font-bold uppercase tracking-wider text-xs flex items-center gap-2" data-testid="pf-add-video"><Plus className="w-3 h-3"/>URL</button>
              </div>
              <label className="block border-2 border-dashed border-slate-900/30 p-4 text-center cursor-pointer hover:border-slate-900 transition-colors text-sm" data-testid="pf-video-upload-zone">
                <VideoIcon className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                <span className="font-semibold">{uploadingVideo ? "Upload vidéo en cours..." : "Ou uploader un fichier vidéo (mp4, webm, mov · max 100 Mo)"}</span>
                <input type="file" accept="video/*" onChange={e => e.target.files[0] && uploadVideoFile(e.target.files[0])} className="hidden" />
              </label>
              {form.videos.length > 0 && (
                <div className="space-y-2 mt-3">
                  {form.videos.map((v, i) => (
                    <div key={v.url || `video-${i}`} className="flex items-center justify-between border border-slate-900/15 p-3 text-sm" data-testid={`video-item-${i}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="tag shrink-0">{v.kind}</span>
                        <span className="font-mono truncate">{v.url}</span>
                      </div>
                      <button type="button" onClick={() => removeVideo(i)} className="text-red-600 ml-3 shrink-0"><X className="w-4 h-4"/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Field>

          <div className="flex gap-6 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.modular} onChange={e => set("modular", e.target.checked)} className="w-4 h-4 accent-slate-900" />
              <span className="text-sm font-semibold">Modulable</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.available} onChange={e => set("available", e.target.checked)} className="w-4 h-4 accent-slate-900" data-testid="pf-available" />
              <span className="text-sm font-semibold">Afficher publiquement</span>
            </label>
          </div>

          <button disabled={saving} type="submit" data-testid="pf-submit"
            className="btn-brutal btn-brutal-solar w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-3 disabled:opacity-50">
            {saving ? "Enregistrement..." : (id ? "Mettre à jour" : "Créer le local")} {!saving && <Save className="w-4 h-4" />}
          </button>
        </form>
      </section>
      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">{label}</span>
    {children}
  </label>
);
